import { Component, OnInit, Inject } from '@angular/core';
import { PolicyClient } from '../../api/benefits-clients';
import { BehaviorSubject } from 'rxjs';
import { UserContext } from '../../services/user-context/user-context.types';
import { UserContextService } from '../../services/user-context/user-context.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DataLabel } from '@benefitsui/app/shared/interfaces/IDataLabel';
import { Clipboard } from '@angular/cdk/clipboard';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';

declare global {
  interface QsiInterface {
    API: {
      unload: () => void;
      load: () => Promise<void>;
      run: () => PromiseLike<void> | void | null | undefined;
    };
  }

  interface Window {
    QSI?: QsiInterface;
  }
}
@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss'],
  providers: [UserContextService]
})
export class DashboardPageComponent implements OnInit {
  constructor(
    private userContextService: UserContextService,
    private policyClient: PolicyClient,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private clipboard: Clipboard,
    @Inject(WINDOW) private window: Window
  ) {
    this.userContext = this.userContextService.get();
    this.userFirstName = this.userContext.currentUserFirstName;
  }

  //constants
  MissingContactText = 'Not seeing who you need?';
  ContactUs = 'Contact us now';
  Welcome = 'Welcome';
  Group = 'Group';
  QuickLinksHeader = 'Helpful quick-links';
  HeroAltText =
    'Introducing our new Benefits Portal! More features coming next year. Check back regularly for exciting updates to your experience.';
  quickLinkIcon = 'open_in_new';
  copyIcon = 'content_copy';
  employerQuickLink = {
    title: "Symetra's Benefits products",
    text: 'Visit Symetra.com to explore our products for employers',
    link: 'https://www.symetra.com/our-products/employers'
  };
  employeeQuickLink = {
    title: 'Employee education & knowledge',
    text: 'Visit Symetra.com for employee education resources',
    link: 'https://www.symetra.com/our-products/for-employees'
  };
  podcastQuickLink = {
    title: 'Working Forward Podcast',
    text: 'Visit our podcast to watch new episodes',
    link: 'https://www.symetra.com/our-products/employers/working-forward-podcast'
  };
  accountManager = 'Account Manager';
  token = '';
  userContext: UserContext;
  userFirstName: string;
  overviewData!: OverviewData;
  contactData!: ContactData[];
  isLoading = true;
  hasContacts = true;

  formatDate(date: string | undefined): string {
    if (!date) {
      return '';
    }
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString();
  }

  copyEmail(email: string) {
    const pending = this.clipboard.beginCopy(email);
    let remainingAttempts = 3;
    const attempt = () => {
      const result = pending.copy();
      if (!result && --remainingAttempts) {
        setTimeout(attempt);
      } else {
        pending.destroy();
      }
    };
    attempt();
  }

  $hasPolicyData: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  ngOnInit(): void {
    this.policyClient.overview().subscribe(
      (response) => {
        const { data } = response;
        if (response.data?.policyDetails === null) {
          this.$hasPolicyData.next(false);
          return;
        }
        this.overviewData = {
          ...this.overviewData,
          policyToken: data?.policyToken ?? '',
          policyNumber: data?.groupNumber ?? '',
          policyHolder: data?.policyDetails?.policyHolder ?? '',
          status: { data: data?.policyDetails?.status ?? '', label: 'Status' },
          effectiveDate: { data: this.formatDate(data?.policyDetails?.effectiveDate), label: 'Effective date' },
          nextRenewalDate: { data: this.formatDate(data?.policyDetails?.nextRenewalDate), label: 'Next renewal date' }
        };
        this.contactData = (data?.contactInfo ?? []).map((rsp) => {
          return {
            contactType: rsp.accountType ?? '',
            name: rsp.accountManagerName ?? '',
            email: rsp.accountManagerEmail ?? '',
            phoneNumber: rsp.accountManagerPhone ?? '',
            primaryButton: (rsp.accountType ?? '') === this.accountManager,
            showDivider: (rsp.accountType ?? '') === this.accountManager
          };
        });

        if (this.contactData.length === 0) {
          this.hasContacts = false;
        }
      },
      () => {},
      () => {
        this.token = this.overviewData.policyToken ?? '';
        const queryParams: Params = { token: this.token };
        if (this.router) {
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      }
    );
    this.window.QSI?.API.run();
  }
}

export interface TooltipContent {
  body: string;
  heading: string;
}
export interface OverviewData {
  policyToken: string;
  policyNumber: string;
  policyHolder: string;
  status: DataLabel;
  effectiveDate: DataLabel;
  nextRenewalDate: DataLabel;
}

export interface ContactData {
  contactType: string;
  name: string;
  email: string;
  phoneNumber: string;
  primaryButton: boolean;
  showDivider: boolean;
}
